import { Injectable, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AuthService } from '../../auth/services/auth.service';
import { InstanceConfigService } from '../../../core/services/instance-config.service';
import { ServicioTecnicoService } from './servicio-tecnico.service';

/**
 * Los mismos de la ruta del taller. TECNICO faltaba: su menú mostraba el
 * contador, pero el servicio nunca preguntaba y quedaba siempre en cero.
 */
const ROLES = ['ROLE_OWNER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_OPERATOR', 'ROLE_TECNICO',
  'ROLE_SUPERVISOR_SERVICIO'];

/** Cada cuánto, como mucho, se vuelve a preguntar. */
const ESPERA_MINIMA_MS = 60_000;

/**
 * Las dos colas de acción de las órdenes de trabajo, para el contador del menú.
 *
 * <ul>
 *   <li><b>Por recibir</b>: máquinas que llegaron y nadie recibió. Es la
 *       cola del taller (técnico, operador).</li>
 *   <li><b>Por revisar</b>: el taller avisó que están listas y esperan que
 *       administración las revise y entregue. Es la cola de gestión.</li>
 * </ul>
 *
 * <p>Salen de la misma consulta (`/ordenes-trabajo/stats`); cuál se muestra
 * lo decide el menú según quién mira. Igual que
 * {@link SolicitudesPendientesService} con las solicitudes.
 *
 * <p><b>Por qué las de recepción y no todas las abiertas.</b> Es una cola de
 * acción: una máquina en recepción está esperando que alguien la reciba, y una
 * en reparación de tres días no espera a nadie. Contar las dos juntas hace que
 * el número deje de leerse.
 *
 * <p>Y es la cola que estaba invisible. El 18-sep-2026 había 5 de 7 órdenes de
 * QAS atascadas en recepción con el trabajo hecho: la orden nace ahí cuando
 * viene de una solicitud del portal —la máquina todavía no llegó— y nada
 * avisaba que había que recibirla.
 *
 * <p>Se refresca al navegar y no con un temporizador, por lo mismo que el de
 * solicitudes: el taller navega todo el día y así no queda un reloj corriendo.
 * Sentado en la misma pantalla el número no sube solo, que es aceptable para
 * una cola que se atiende varias veces al día.
 */
@Injectable({ providedIn: 'root' })
export class OrdenesEnRecepcionService {
  private servicio = inject(ServicioTecnicoService);
  private auth = inject(AuthService);
  private instanceConfig = inject(InstanceConfigService);
  private router = inject(Router);

  private readonly enRecepcion = signal<number>(0);
  private readonly listas = signal<number>(0);

  /** Cuántas esperan que alguien las reciba. `0` también significa «sin datos». */
  readonly cantidad = this.enRecepcion.asReadonly();
  /** Cuántas avisó el taller que están listas y nadie revisó todavía. */
  readonly porRevisar = this.listas.asReadonly();

  private ultimaConsulta = 0;
  private enVuelo = false;

  constructor() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.refrescar());

    // La configuración de instancia llega después del arranque, y hasta que
    // llega `puedeConsultar()` dice que no. Sin esto el contador se queda en
    // cero hasta la primera navegación: quien entra y se queda en la misma
    // pantalla no se entera de nada, que es justo lo que el contador viene a
    // evitar.
    effect(() => {
      if (this.instanceConfig.muestraServicioTecnico()) this.refrescar();
    });

    this.refrescar();
  }

  /**
   * Vuelve a preguntar, con tope de tiempo.
   *
   * <p>`forzar` lo usa la pantalla que acaba de cambiar el número —recibir una
   * máquina—: ahí esperar el minuto dejaría el contador contradiciendo lo que
   * la persona acaba de hacer.
   */
  refrescar(forzar = false): void {
    if (!this.puedeConsultar()) return;
    if (this.enVuelo) return;

    const ahora = Date.now();
    if (!forzar && ahora - this.ultimaConsulta < ESPERA_MINIMA_MS) return;

    this.ultimaConsulta = ahora;
    this.enVuelo = true;

    this.servicio.stats().subscribe({
      next: (res) => {
        this.enVuelo = false;
        if (res.success && res.data) {
          this.enRecepcion.set(res.data.enRecepcion ?? 0);
          this.listas.set(res.data.listasParaEntrega ?? 0);
        }
      },
      // Va por getSilent: un contador que no cargó no merece un aviso de error.
      error: () => { this.enVuelo = false; },
    });
  }

  /** Olvida el número al cerrar sesión: el siguiente no hereda el del anterior. */
  limpiar(): void {
    this.enRecepcion.set(0);
    this.listas.set(0);
    this.ultimaConsulta = 0;
  }

  /**
   * Preguntar solo si la respuesta puede ser algo distinto de un 403.
   *
   * <p>Sin sesión, sin el rol, o con el taller apagado, la consulta sobra —y
   * en el caso del cliente del portal sería además un 403 en cada navegación.
   */
  private puedeConsultar(): boolean {
    return this.auth.isAuthenticated()
      && this.auth.hasAnyRole(ROLES)
      && this.instanceConfig.muestraServicioTecnico();
  }
}
